/**
 * 「这次带给 AI 的原文」——(2026-08-13)挂在各目的地蓝图条下面的只读面板。
 *
 * 用户原话:「五个目的地都没有显示实际传给地基的值」。结构层其实一直真传过去了
 * (payload → ai FormulaCard → prompt_block 注入架构师),只是走的是看不见的通道。
 * 这里把引擎真正读到的那段原文原样摊开 —— 所见即所送。
 *
 * ★只读、可折叠、可复制:它是「查证」信息,不该抢走编辑框的位置;
 *   默认折叠只留一行摘要,点开才摊全文。
 * ★与内容层分开:这块**只放结构层**(套路/节拍/公式/槽位/坑/CTA)。
 *   参考片的原话(爆点、逐字口播)属于内容层,归各目的地自己的编辑区管,不混进来 ——
 *   混在一起用户就分不清哪些是自己的、哪些是别人的(0813 统一口径的由来)。
 */
import { Check, ChevronDown, Copy, FileCode2 } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

import type { FormulaPayload } from "../model/types";
import { buildPromptPreview, styleAffinityOf } from "../model/promptPreview";

export function HandoffPreview({
  payload,
  className,
}: {
  payload: FormulaPayload | null | undefined;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = buildPromptPreview(payload);
  if (!text) return null;
  const { fit, conflict } = styleAffinityOf(payload);
  const lineCount = text.split("\n").filter(Boolean).length;

  const copy = () => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div
      data-testid="handoff-preview"
      className={`rounded-lg border border-border bg-muted/20 ${className ?? ""}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <FileCode2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <button
          type="button"
          data-testid="handoff-preview-toggle"
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-medium hover:text-primary"
          onClick={() => setOpen((v) => !v)}
        >
          这次带给 AI 的原文
          <span className="text-2xs font-normal text-muted-foreground">
            {lineCount} 条 · 引擎按这个编排
          </span>
          <ChevronDown
            className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 shrink-0 gap-1 px-2 text-2xs"
            onClick={copy}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "已复制" : "复制"}
          </Button>
        )}
      </div>
      {open && (
        <div className="border-t border-border px-3 py-2.5">
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-2xs leading-relaxed text-foreground">
            {text}
          </pre>
          {(fit.length > 0 || conflict.length > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
              <span className="text-2xs text-muted-foreground">参考片风格</span>
              {fit.map((s) => (
                <Badge key={s} variant="outline" className="h-5 gap-0.5 text-2xs">
                  ✓ {s}
                </Badge>
              ))}
              {conflict.map((s) => (
                <Badge
                  key={s}
                  variant="outline"
                  className="h-5 gap-0.5 text-2xs text-muted-foreground"
                >
                  ✗ {s}
                </Badge>
              ))}
            </div>
          )}
          <p className="mt-2 text-2xs leading-relaxed text-muted-foreground">
            这段是结构层,会随这次生成一起交给引擎;你自己的产品事实写在下面的编辑区, 两者分开 ——
            参考片说过的话不会进你的成品。
          </p>
        </div>
      )}
    </div>
  );
}
